/**
 * Copyright (c) 2020-2021 Perficient, Inc.
 * Unauthorized copying or distribution of this file prohibited without the express permission of Perficient.
 */

import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Dialog, DialogActions, DialogContent, DialogTitle, Chip, Tooltip } from "@material-ui/core";
import ChipInput from "../../../../components/ChipInput/ChipInput";
import { useExternalContacts } from "../../context/externalContacts";
import FormTextField from "../../../../components/FormTextField/FormTextField";
import { isEmptyObject } from "../../../../utility/helpers";
import { EXTERNAL_CONTACT_TOOLTIPS } from "../../../../utility/toolTipMessages";
import { ActionButtons } from "../../../../components/Common";

const { CONTACT_NAME, CONTACT_PHONE, CONTACT_TAGS, CONTACT_AZURE_AD_USER_ID } = EXTERNAL_CONTACT_TOOLTIPS;

/**
 * Modal form used for adding and editing contacts
 * @param {Object} props
 * @param {Boolean} props.isOpen used to control open/closed state of the modal
 */
export const ExternalContactEdit = ({ isLoading, setIsLoading, isOpen }) => {
	const { contactToEdit, cancelEditContact, validateContact, saveContact } = useExternalContacts();
	const [contact, setContact] = useState({});
	const [validation, setValidation] = useState({ errors: {}, isValid: true });
	const [isEdited, setIsEdited] = useState(false);

	const isNew = useMemo(() => contact.contactId === null, [contact.contactId]);

	const resetState = useCallback(() => {
		setContact({});
		setValidation({ errors: {}, isValid: true });
		setIsEdited(false);
		setIsLoading(false);
	}, [setIsLoading]);

	const updateProperty = useCallback(
		(prop, value) => {
			setContact({ ...contact, [prop]: value });
		},
		[contact],
	);

	const getError = useCallback(
		(prop) => {
			if (!isEdited) {
				return "";
			}
			return validation.errors[prop];
		},
		[isEdited, validation],
	);

	const handleSave = useCallback(async () => {
		try {
			setIsLoading(true);
			await saveContact({ ...contact });
		} catch (_) {}
		setIsLoading(false);
	}, [contact, saveContact, setIsLoading]);

	useEffect(() => {
		if (!isEmptyObject(contact)) {
			setValidation(validateContact(contact));
		}
	}, [contact, validateContact, isEdited]);

	useEffect(() => {
		if (!isOpen) {
			resetState();
		}
	}, [isOpen, resetState]);

	useEffect(() => {
		setContact({ ...contactToEdit });
	}, [contactToEdit]);

	return (
		<div>
			{contact && (
				<Dialog open={isOpen} onClose={cancelEditContact} fullWidth>
					<DialogTitle>{isNew ? "Create new contact" : "Edit contact"}</DialogTitle>
					<DialogContent>
						<div className="d-flex mb-4">
							<FormTextField
								autoFocus
								label={"Name*"}
								formEdited={isEdited}
								setEdited={setIsEdited}
								errorMessage={getError("fullName")}
								startingValue={contact.fullName || ""}
								setConfigValue={updateProperty}
								valueName={"fullName"}
								styleObject={{ minWidth: 120, flex: 1 }}
								enableDebounce
								errorCheck={() => !!getError("fullName")}
								toolTipMessage={CONTACT_NAME}
							/>
						</div>

						<div className="d-flex mb-4">
							<FormTextField
								label={"Phone number"}
								formEdited={isEdited}
								setEdited={setIsEdited}
								errorMessage={getError("phone")}
								startingValue={contact.phone || ""}
								setConfigValue={updateProperty}
								valueName={"phone"}
								styleObject={{ minWidth: 120, flex: 1 }}
								enableDebounce
								errorCheck={() => !!getError("phone")}
								placeholder="e.g. +14155552671"
								toolTipMessage={CONTACT_PHONE}
							/>
						</div>

						<div className="d-flex mb-4 position-relative">
							<div style={{ position: "absolute", bottom: "5px", zIndex: 1 }}>
								<Tooltip arrow interactive placement={"bottom-start"} title={CONTACT_TAGS}>
									<i style={{ height: "15px", color: "gray" }} className="fas fa-info-circle" />
								</Tooltip>
							</div>
							<ChipInput
								blurBehavior="add-or-clear"
								allowDuplicates
								style={{ paddingLeft: "25px" }}
								error={!!getError("tags")}
								helperText={getError("tags")}
								label="Tags"
								fullWidth
								chipRenderer={customChipRenderer}
								value={contact.tags}
								onAdd={(tag) => {
									updateProperty("tags", [...contact.tags, tag]);
									setIsEdited(true);
								}}
								onDelete={(tag) => {
									updateProperty(
										"tags",
										contact.tags.filter((t) => t !== tag),
									);
									setIsEdited(true);
								}}
								InputLabelProps={{
									shrink: true,
								}}
							/>
						</div>

						<div className="d-flex mb-4">
							<FormTextField
								label={"Azure Active Directory User ID"}
								formEdited={isEdited}
								setEdited={setIsEdited}
								errorMessage={getError("azureAdUserId")}
								startingValue={contact.azureAdUserId || ""}
								setConfigValue={updateProperty}
								valueName={"azureAdUserId"}
								styleObject={{ minWidth: 120, flex: 1 }}
								enableDebounce
								errorCheck={() => !!getError("azureAdUserId")}
								toolTipMessage={CONTACT_AZURE_AD_USER_ID}
							/>
						</div>
					</DialogContent>
					<DialogActions style={{ padding: "0 20px 15px 0" }}>
						<ActionButtons
							showCancel
							showSave
							disableSave={!validation.isValid || isLoading}
							onSave={handleSave}
							onCancel={cancelEditContact}
							saveLabel={isNew ? "Create Contact" : "Save Contact"}
						/>
					</DialogActions>
				</Dialog>
			)}
		</div>
	);
};

const customChipRenderer = (
	{ _value, text, isFocused, isDisabled, isReadOnly, handleClick, handleDelete, className },
	key,
) => (
	<Chip
		key={key}
		className={className}
		variant="outlined"
		style={{
			pointerEvents: isDisabled || isReadOnly ? "none" : undefined,
			backgroundColor: isFocused ? "rgba(0, 0, 0, 0.04)" : undefined,
		}}
		onClick={handleClick}
		onDelete={handleDelete}
		label={text}
	/>
);
